import {announce} from '@github-ui/aria-live'
import {CopilotAppIcon} from '@github-ui/copilot-avatar/CopilotAppIcon'
import {ActionList} from '@primer/react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type Ref,
  type CSSProperties,
} from 'react'

import {
  DEFAULT_DEEP_LINK,
  defaultLaunchDeepLink,
  isValidAcceptedDeepLink,
  resolveLaunchTarget,
  useDeepLinkLaunch,
  type LaunchDeepLink,
  type LaunchDisplayState,
} from '../deep-link'
import {
  type CopilotAppLauncherEventName,
  getCopilotAppLauncherTargetTelemetry,
  sanitizeEntryPoint,
  sendCopilotAppLauncherEvent,
  type CopilotAppLauncherTelemetryContext,
} from '../telemetry'
import {GITHUB_APP_LANDING_PAGE_URL} from '../urls'

import styles from './CopilotAppOpenAction.module.css'

export type CopilotAppOpenActionVariant = 'action-list-item'
export type CopilotAppOpenActionDisplayState = 'resting' | Exclude<LaunchDisplayState, 'success'>

export interface CopilotAppOpenActionProps {
  entryPoint: string
  open: string | null | undefined
  label?: string
  variant?: CopilotAppOpenActionVariant
  displayState?: CopilotAppOpenActionDisplayState
  launchDeepLink?: LaunchDeepLink
  ref?: Ref<HTMLLIElement>
}

interface UseCopilotAppOpenActionOptions {
  entryPoint: string
  open: string | null | undefined
  displayState?: CopilotAppOpenActionDisplayState
  launchDeepLink: LaunchDeepLink
  ref?: Ref<HTMLLIElement>
}

type ActionSelectEvent = MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>

export const COPILOT_APP_OPEN_ACTION_OPENING_STATUS_MESSAGE = 'Opening GitHub Copilot app'
export const COPILOT_APP_OPEN_ACTION_EXPIRED_STATUS_MESSAGE =
  'GitHub Copilot app did not open. Use Open the app to retry or Download to install.'
export const COPILOT_APP_OPEN_ACTION_LAUNCH_DELAY_MS = 500
export const RESTING_RESET_DELAY_MS = 1000
export const EXPIRED_AUTO_RESET_DELAY_MS = 10000
const copilotAppIconViewportStyle = {
  width: 'var(--base-size-20)',
  height: 'var(--base-size-20)',
} satisfies CSSProperties

interface ScheduleRestingResetOptions {
  shouldRestoreFocus?: () => boolean
  shouldDeferReset?: () => boolean
}

function alwaysFalse() {
  return false
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return

  if (typeof ref === 'function') {
    ref(value)
  } else {
    ref.current = value
  }
}

function CopilotAppIconViewport() {
  return (
    <span className="d-inline-flex flex-items-center flex-justify-center" style={copilotAppIconViewportStyle}>
      <CopilotAppIcon />
    </span>
  )
}

function useCopilotAppOpenAction({
  entryPoint,
  open,
  displayState,
  launchDeepLink,
  ref,
}: UseCopilotAppOpenActionOptions) {
  const [attemptedLaunchTarget, setAttemptedLaunchTarget] = useState<string | null>(null)
  const actionItemRef = useRef<HTMLLIElement | null>(null)
  const shouldFocusActionItemRef = useRef(false)
  const shouldFocusExpiredRetryRef = useRef(false)
  const isExpiredPointerInsideRef = useRef(false)
  const restingResetTimerRef = useRef<number | undefined>(undefined)
  const launchTarget = useMemo(() => (open && isValidAcceptedDeepLink(open) ? resolveLaunchTarget(open) : null), [open])
  const hasValidLaunchTarget = launchTarget !== null
  const sanitizedEntryPoint = useMemo(() => sanitizeEntryPoint(entryPoint), [entryPoint])
  const targetTelemetry = useMemo(() => getCopilotAppLauncherTargetTelemetry(open ?? null), [open])
  const telemetryContext = useMemo<CopilotAppLauncherTelemetryContext>(
    () => ({
      entry_point: sanitizedEntryPoint,
      state: 'waiting',
      ...targetTelemetry,
    }),
    [sanitizedEntryPoint, targetTelemetry],
  )
  const trackLauncherEvent = useCallback(
    (eventName: CopilotAppLauncherEventName, context: Partial<CopilotAppLauncherTelemetryContext> = {}) => {
      sendCopilotAppLauncherEvent(eventName, {
        ...telemetryContext,
        ...context,
      })
    },
    [telemetryContext],
  )
  const handleManualLaunchAttempt = useCallback(
    (trigger: 'manual' | 'retry', state: CopilotAppLauncherTelemetryContext['state']) => {
      trackLauncherEvent('open_manual', {state, trigger})
    },
    [trackLauncherEvent],
  )
  const handleLaunchExpired = useCallback(() => {
    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement && actionItemRef.current?.contains(activeElement)) {
      shouldFocusExpiredRetryRef.current = true
    }

    announce(COPILOT_APP_OPEN_ACTION_EXPIRED_STATUS_MESSAGE)
  }, [])
  const forcedLaunchDisplayState = displayState === 'resting' ? undefined : displayState
  const {visibleState, handleRetry, resetLaunch} = useDeepLinkLaunch({
    target: launchTarget ?? DEFAULT_DEEP_LINK,
    displayState: forcedLaunchDisplayState,
    launchDeepLink,
    launchDelayMs: COPILOT_APP_OPEN_ACTION_LAUNCH_DELAY_MS,
    shouldAutoLaunch: false,
    onManualLaunchAttempt: handleManualLaunchAttempt,
    onLaunchExpired: displayState === undefined ? handleLaunchExpired : undefined,
  })
  const launchState = displayState ?? (attemptedLaunchTarget === launchTarget ? visibleState : 'resting')
  const actionState = launchState === 'success' ? 'waiting' : launchState

  const clearRestingResetTimer = useCallback(() => {
    if (restingResetTimerRef.current === undefined) return

    window.clearTimeout(restingResetTimerRef.current)
    restingResetTimerRef.current = undefined
  }, [])

  const resetToResting = useCallback(
    (shouldRestoreFocus = false) => {
      clearRestingResetTimer()
      if (shouldRestoreFocus) {
        shouldFocusActionItemRef.current = true
      }
      resetLaunch()
      setAttemptedLaunchTarget(currentTarget => (currentTarget === launchTarget ? null : currentTarget))
    },
    [clearRestingResetTimer, launchTarget, resetLaunch],
  )

  const scheduleRestingReset = useCallback(
    (
      delayMs: number,
      {shouldRestoreFocus = alwaysFalse, shouldDeferReset = alwaysFalse}: ScheduleRestingResetOptions = {},
    ) => {
      if (displayState !== undefined) return () => {}

      clearRestingResetTimer()
      let activeTimer: number | undefined
      let isCancelled = false

      const scheduleTimer = () => {
        if (isCancelled) return

        const resetTimer = window.setTimeout(() => {
          if (isCancelled || restingResetTimerRef.current !== resetTimer) return

          restingResetTimerRef.current = undefined
          activeTimer = undefined
          if (shouldDeferReset()) {
            scheduleTimer()
            return
          }

          resetToResting(shouldRestoreFocus())
        }, delayMs)
        activeTimer = resetTimer
        restingResetTimerRef.current = resetTimer
      }

      scheduleTimer()

      return () => {
        isCancelled = true
        if (activeTimer === undefined || restingResetTimerRef.current !== activeTimer) return

        window.clearTimeout(activeTimer)
        restingResetTimerRef.current = undefined
      }
    },
    [clearRestingResetTimer, displayState, resetToResting],
  )

  const shouldRestoreFocusFromExpiredItem = useCallback(() => {
    const activeElement = document.activeElement
    return activeElement instanceof HTMLElement && actionItemRef.current?.contains(activeElement) === true
  }, [])
  const shouldDeferExpiredAutoReset = useCallback(
    () => shouldRestoreFocusFromExpiredItem() || isExpiredPointerInsideRef.current,
    [shouldRestoreFocusFromExpiredItem],
  )

  useEffect(() => clearRestingResetTimer, [clearRestingResetTimer])

  useEffect(() => {
    if (displayState !== undefined || launchState !== 'success') return

    return scheduleRestingReset(RESTING_RESET_DELAY_MS)
  }, [displayState, launchState, scheduleRestingReset])

  useEffect(() => {
    if (displayState !== undefined || actionState !== 'expired') return

    return scheduleRestingReset(EXPIRED_AUTO_RESET_DELAY_MS, {
      shouldRestoreFocus: shouldRestoreFocusFromExpiredItem,
      shouldDeferReset: shouldDeferExpiredAutoReset,
    })
  }, [actionState, displayState, scheduleRestingReset, shouldDeferExpiredAutoReset, shouldRestoreFocusFromExpiredItem])

  const openDownload = useCallback(() => {
    trackLauncherEvent('download_clicked', {state: 'expired'})
    scheduleRestingReset(RESTING_RESET_DELAY_MS, {shouldRestoreFocus: shouldRestoreFocusFromExpiredItem})
    window.open(GITHUB_APP_LANDING_PAGE_URL, '_blank', 'noopener,noreferrer')
  }, [scheduleRestingReset, shouldRestoreFocusFromExpiredItem, trackLauncherEvent])
  const handleSelect = useCallback(
    (event: ActionSelectEvent) => {
      event.preventDefault()
      clearRestingResetTimer()

      if (actionState === 'waiting') {
        return
      }

      if (displayState === undefined) {
        announce(COPILOT_APP_OPEN_ACTION_OPENING_STATUS_MESSAGE)
      }
      if (actionState === 'expired') {
        shouldFocusActionItemRef.current = true
      }
      setAttemptedLaunchTarget(launchTarget)
      handleRetry(actionState === 'expired' ? 'retry' : 'manual')
    },
    [actionState, clearRestingResetTimer, displayState, handleRetry, launchTarget],
  )
  const handleDownloadClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (actionItemRef.current?.contains(event.currentTarget)) {
        shouldFocusActionItemRef.current = true
      }
      openDownload()
    },
    [openDownload],
  )
  const handleExpiredBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const nextFocusedElement = event.relatedTarget
      if (nextFocusedElement instanceof Node && event.currentTarget.contains(nextFocusedElement)) return

      scheduleRestingReset(RESTING_RESET_DELAY_MS)
    },
    [scheduleRestingReset],
  )
  const handleExpiredFocus = useCallback(() => {
    scheduleRestingReset(EXPIRED_AUTO_RESET_DELAY_MS, {
      shouldRestoreFocus: shouldRestoreFocusFromExpiredItem,
      shouldDeferReset: shouldDeferExpiredAutoReset,
    })
  }, [scheduleRestingReset, shouldDeferExpiredAutoReset, shouldRestoreFocusFromExpiredItem])
  const handleExpiredMouseEnter = useCallback(() => {
    isExpiredPointerInsideRef.current = true
    scheduleRestingReset(EXPIRED_AUTO_RESET_DELAY_MS, {
      shouldRestoreFocus: shouldRestoreFocusFromExpiredItem,
      shouldDeferReset: shouldDeferExpiredAutoReset,
    })
  }, [scheduleRestingReset, shouldDeferExpiredAutoReset, shouldRestoreFocusFromExpiredItem])
  const handleExpiredMouseLeave = useCallback(() => {
    isExpiredPointerInsideRef.current = false
    scheduleRestingReset(EXPIRED_AUTO_RESET_DELAY_MS, {
      shouldRestoreFocus: shouldRestoreFocusFromExpiredItem,
      shouldDeferReset: shouldDeferExpiredAutoReset,
    })
  }, [scheduleRestingReset, shouldDeferExpiredAutoReset, shouldRestoreFocusFromExpiredItem])
  const setActionItemRef = useCallback(
    (node: HTMLElement | null) => {
      const itemElement = node?.closest('li') as HTMLLIElement | null
      actionItemRef.current = itemElement
      assignRef(ref, itemElement)

      if (node && shouldFocusActionItemRef.current) {
        shouldFocusActionItemRef.current = false
        node.focus()
      }
    },
    [ref],
  )
  const setExpiredRetryButtonRef = useCallback((node: HTMLButtonElement | null) => {
    if (node && shouldFocusExpiredRetryRef.current) {
      shouldFocusExpiredRetryRef.current = false
      node.focus()
    }
  }, [])

  return {
    actionState,
    handleDownloadClick,
    handleExpiredBlur,
    handleExpiredFocus,
    handleExpiredMouseEnter,
    handleExpiredMouseLeave,
    handleSelect,
    hasValidLaunchTarget,
    setActionItemRef,
    setExpiredRetryButtonRef,
  }
}

interface CopilotAppOpenActionLabelProps {
  label: string
  state: CopilotAppOpenActionDisplayState
}

function OpeningLabel({isShimmering}: {isShimmering: boolean}) {
  return (
    <span className={styles.OpeningLabel}>
      {isShimmering ? (
        <span className={styles.OpeningWordShimmer}>
          <span className={styles.OpeningWordInner}>Opening</span>
        </span>
      ) : (
        <span className={styles.OpeningWord}>Opening</span>
      )}
      <span>GitHub Copilot app</span>
    </span>
  )
}

function CopilotAppOpenActionLabel({label, state}: CopilotAppOpenActionLabelProps) {
  let content
  if (state === 'waiting') {
    content = <OpeningLabel isShimmering />
  } else {
    content = label
  }

  return (
    <span key={state} className={styles.LabelTransition} data-state={state}>
      {content}
    </span>
  )
}

interface ExpiredActionItemProps {
  onRetry: (event: MouseEvent<HTMLElement>) => void
  onDownloadClick: (event: MouseEvent<HTMLButtonElement>) => void
  onBlur: (event: FocusEvent<HTMLElement>) => void
  onFocus: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  ref?: Ref<HTMLLIElement>
  retryButtonRef?: Ref<HTMLButtonElement>
}

function ExpiredActionItem({
  onRetry,
  onDownloadClick,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  ref,
  retryButtonRef,
}: ExpiredActionItemProps) {
  return (
    <li
      ref={ref}
      className={styles.ExpiredActionRow}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span className={styles.ExpiredIcon}>
        <CopilotAppIcon />
      </span>
      <span className={styles.ExpiredContent}>
        <button type="button" className={styles.ExpiredRetryButton} onClick={onRetry} ref={retryButtonRef}>
          Open the app
        </button>
        <span className={styles.ExpiredDescription}>
          <span>Don&apos;t have the app?</span>{' '}
          <button type="button" className={styles.DownloadButton} onClick={onDownloadClick}>
            Download
          </button>
        </span>
      </span>
    </li>
  )
}

export function CopilotAppOpenAction({
  entryPoint,
  open,
  label = 'Open in GitHub Copilot app',
  variant = 'action-list-item',
  displayState,
  launchDeepLink = defaultLaunchDeepLink,
  ref,
}: CopilotAppOpenActionProps) {
  const {
    actionState,
    handleDownloadClick,
    handleExpiredBlur,
    handleExpiredFocus,
    handleExpiredMouseEnter,
    handleExpiredMouseLeave,
    handleSelect,
    hasValidLaunchTarget,
    setActionItemRef,
    setExpiredRetryButtonRef,
  } = useCopilotAppOpenAction({
    entryPoint,
    open,
    displayState,
    launchDeepLink,
    ref,
  })

  if (!hasValidLaunchTarget) return null

  switch (variant) {
    case 'action-list-item':
      if (actionState === 'expired') {
        return (
          <ExpiredActionItem
            ref={setActionItemRef}
            retryButtonRef={setExpiredRetryButtonRef}
            onRetry={handleSelect}
            onDownloadClick={handleDownloadClick}
            onBlur={handleExpiredBlur}
            onFocus={handleExpiredFocus}
            onMouseEnter={handleExpiredMouseEnter}
            onMouseLeave={handleExpiredMouseLeave}
          />
        )
      }

      return (
        <ActionList.Item onSelect={handleSelect} ref={setActionItemRef}>
          <ActionList.LeadingVisual>
            <CopilotAppIconViewport />
          </ActionList.LeadingVisual>
          <CopilotAppOpenActionLabel label={label} state={actionState} />
        </ActionList.Item>
      )
    default:
      return null
  }
}

CopilotAppOpenAction.displayName = 'CopilotAppOpenAction'
